import { useState } from 'react'
import { Link } from 'react-router-dom'
import BookingModal from '../../components/BookingModal'
import FontAwesomeIcon from '../../components/FontAwesomeIcon'

export default function SipiFallsAbseiling() {
  const [isModalOpen, setIsModalOpen] = useState(false)

  return (
    <div>
      {/* Hero Section */}
      <section
        className="relative h-96 md:h-[500px] bg-cover bg-center flex items-center"
        style={{
          backgroundImage: 'linear-gradient(rgba(0,0,0,0.4), rgba(0,0,0,0.4)), url(https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=1600)',
        }}
      >
        <div className="container mx-auto px-4">
          <div className="max-w-4xl text-white">
            <h1 className="text-3xl md:text-5xl font-bold mb-3 font-heading">
              Sipi Falls Abseiling Adventure
            </h1>
            <p className="text-xl md:text-2xl mb-6">
              Rappel down the face of a 100-meter waterfall in the foothills of Mt. Elgon
            </p>
            <div className="flex items-center gap-6 mb-6">
              <div className="flex items-center gap-2">
                <div className="flex text-safari-gold">
                  {[...Array(5)].map((_, i) => (
                    <FontAwesomeIcon key={i} icon="fa-star" className="w-5 h-5" />
                  ))}
                </div>
                <span className="text-lg">(200+ reviews)</span>
              </div>
            </div>
            <div className="flex flex-wrap gap-4 mb-8">
              <div className="bg-white/90 backdrop-blur-sm text-safari-green px-4 py-2 rounded-full text-sm font-semibold">
                <FontAwesomeIcon icon="fa-shield-halved" className="w-4 h-4 mr-2" />
                Safety Certified
              </div>
              <div className="bg-white/90 backdrop-blur-sm text-safari-green px-4 py-2 rounded-full text-sm font-semibold">
                <FontAwesomeIcon icon="fa-certificate" className="w-4 h-4 mr-2" />
                Professional Guides
              </div>
              <div className="bg-white/90 backdrop-blur-sm text-safari-green px-4 py-2 rounded-full text-sm font-semibold">
                <FontAwesomeIcon icon="fa-bolt" className="w-4 h-4 mr-2" />
                Adrenaline Rush
              </div>
            </div>
            <button
              onClick={() => setIsModalOpen(true)}
              className="bg-safari-gold hover:bg-safari-gold/90 text-white px-8 py-4 rounded-full text-lg font-bold transition-all duration-300 shadow-lg hover:shadow-xl"
            >
              Book Your Abseiling Adventure
            </button>
          </div>
        </div>
      </section>

      {/* Page Content */}
      <div className="container mx-auto px-4 py-16">
        <div className="lg:grid lg:grid-cols-3 lg:gap-12">
          {/* Main Content */}
          <main className="lg:col-span-2">
            {/* Description Section */}
            <section className="mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-safari-green mb-8">
                Abseil Down Uganda's Most Spectacular Waterfall
              </h2>
              <div className="prose prose-lg max-w-none">
                <p className="text-lg text-gray-700 leading-relaxed mb-6">
                  Sipi Falls abseiling offers one of Uganda's most thrilling adventure activities—rappelling down a 100-meter waterfall on the western slopes of Mount Elgon. This is one of very few places on Earth where you can abseil beside a major waterfall, with the mist, spray, and thundering water creating an unforgettable experience. The activity combines technical rope work with spectacular natural scenery, making it a bucket-list adventure for adrenaline seekers visiting Eastern Uganda.
                </p>
                <p className="text-lg text-gray-700 leading-relaxed mb-6">
                  Located on the western slopes of Mount Elgon in Eastern Uganda, Sipi Falls is actually a series of three magnificent waterfalls tumbling down through lush coffee plantations and highland forest. The main falls—known as Kaptogolo Falls—drops 100 meters (328 feet) in a spectacular single plunge, and this is where the abseiling takes place. The second waterfall, Ngasire Falls, cascades 85 meters (279 feet), while the third, Simba Falls, drops 65 meters (213 feet). Together, they create one of Uganda's most photogenic and adventure-rich destinations.
                </p>
                <h3 className="text-2xl font-bold text-safari-green mt-8 mb-4">The Abseiling Experience</h3>
                <p className="text-lg text-gray-700 leading-relaxed mb-6">
                  The abseiling begins at the top of Kaptogolo Falls where the river plunges over the cliff edge. Certified guides double-check harnesses, attach participants to the rope system with multiple safety redundancies, and provide final instructions on technique and hand signals. The descent involves leaning back over the edge, transferring weight to the harness, and walking backward down the vertical rock face while controlling speed with a brake hand. The waterfall thunders beside you throughout the descent, with spray and mist creating rainbows in the sunlight.
                </p>
                <p className="text-lg text-gray-700 leading-relaxed mb-6">
                  The descent takes 15-30 minutes depending on pace and experience level. First-timers typically take it slow, pausing to adjust to the height and take in the views, while experienced abseilers can control their descent more quickly. Guides manage the rope system from above and wait at the bottom to assist with landing. The activity requires no previous climbing experience, as comprehensive training is provided before the descent begins.
                </p>
                <h3 className="text-2xl font-bold text-safari-green mt-8 mb-4">Coffee Country and Cultural Immersion</h3>
                <p className="text-lg text-gray-700 leading-relaxed">
                  The Sipi Falls area is renowned for producing some of Uganda's finest Arabica coffee, grown on the fertile volcanic slopes of Mount Elgon at altitudes of 1,600-1,900 meters. Abseiling can easily be combined with a coffee tour, where local farmers guide visitors through the entire "seed to cup" process. The tour includes picking ripe coffee cherries, pulping them, drying the beans, roasting them over an open fire using traditional methods, grinding them with a mortar and pestle, and tasting a freshly brewed cup. Many visitors also explore the local Sabiny culture, visit traditional homes, taste local foods like banana beer, and learn about life in this highland farming community. The combination of adrenaline adventure and cultural immersion makes Sipi Falls a truly unique destination.
                </p>
              </div>
            </section>

            {/* Highlights */}
            <section className="mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-safari-green mb-12 text-center">
                Why Abseil Sipi Falls
              </h2>
              <div className="grid md:grid-cols-2 gap-8">
                {[
                  {
                    icon: 'fa-water',
                    title: 'Waterfall Rappelling',
                    description: 'Descend beside 100 meters of cascading water—one of the few places on Earth offering this rare adventure experience alongside a major waterfall.'
                  },
                  {
                    icon: 'fa-bolt',
                    title: 'Ultimate Adrenaline Rush',
                    description: 'Heart-pounding adventure that challenges and exhilarates in equal measure. The combination of height, water, and vertical descent creates an unforgettable thrill.'
                  },
                  {
                    icon: 'fa-mountain',
                    title: 'Spectacular Setting',
                    description: 'Mt. Elgon foothills with panoramic views over Karamoja plains and the Sipi Valley. The landscape is absolutely stunning with coffee plantations and highland forests.'
                  },
                  {
                    icon: 'fa-shield-halved',
                    title: 'Professional Safety Standards',
                    description: 'Certified guides with years of experience, international-standard equipment regularly inspected, and comprehensive safety protocols with multiple redundancies built into every rope system.'
                  },
                  {
                    icon: 'fa-user-graduate',
                    title: 'No Experience Needed',
                    description: 'Full training provided before you start—first-timers are welcome and encouraged! Guides tailor the experience to your comfort level and provide support throughout.'
                  },
                  {
                    icon: 'fa-person-hiking',
                    title: 'Three Waterfalls Circuit',
                    description: 'Visit all three Sipi Falls through scenic coffee plantations and highland forest. Optional swimming in natural pools and coffee tours available to complete your experience.'
                  }
                ].map((highlight, index) => (
                  <div key={index} className="flex gap-4">
                    <div className="flex-shrink-0">
                      <div className="w-12 h-12 bg-safari-green rounded-full flex items-center justify-center">
                        <FontAwesomeIcon icon={highlight.icon} className="w-6 h-6 text-white" />
                      </div>
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-safari-green mb-2">{highlight.title}</h3>
                      <p className="text-gray-700">{highlight.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </section>

            {/* Itinerary */}
            <section className="mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-safari-green mb-8 text-center">
                Sample Activity Itinerary
              </h2>
              <div className="bg-safari-light border-l-4 border-safari-gold p-6 mb-8 rounded-r-lg">
                <p className="text-gray-700 text-lg">
                  <strong>⚠️ Important Note:</strong> This itinerary is tentative and can be customized based on your preferences,
                  pickup location, and weather conditions. Activities can be combined with coffee tours or cultural visits.
                </p>
              </div>
              <div className="space-y-6">
                {[
                  {
                    time: '08:00',
                    title: 'Arrival and Briefing',
                    desc: 'Meet your certified guides at the base, sign safety waivers, and receive a comprehensive safety briefing covering equipment use, descent techniques, and hand signals. Get fitted with harness, helmet, and gloves.'
                  },
                  {
                    time: '09:00',
                    title: 'Hike to the Falls',
                    desc: 'Trek 45-60 minutes through scenic coffee plantations and highland forest to reach the top of the 100-meter Kaptogolo Falls. Your guide explains the local ecosystem and coffee cultivation along the way.'
                  },
                  {
                    time: '10:00',
                    title: 'Training & Setup',
                    desc: 'Guides set up the rope system with multiple safety redundancies. Practice proper technique on a smaller practice section, learning how to control your descent, use the brake hand, and position your body correctly.'
                  },
                  {
                    time: '10:30',
                    title: 'The Abseil!',
                    desc: 'Descend the 100-meter face beside the thundering waterfall—pure adrenaline! Take your time, pause for photos, and soak in the incredible views. Guides manage your safety from above and below throughout the 15-30 minute descent.'
                  },
                  {
                    time: '12:00',
                    title: 'Waterfall Circuit Hike',
                    desc: 'Hike to the second (Ngasire, 85m) and third (Simba, 65m) falls through beautiful highland scenery. Optional swimming in natural pools if weather permits. Learn about the local Sabiny culture and coffee farming traditions.'
                  },
                  {
                    time: '14:00',
                    title: 'Return & Celebration',
                    desc: 'Return to base, receive your certificate of achievement, and share stories of your conquest. Optional coffee tasting or lunch at a local café with views of the falls.'
                  }
                ].map((step, index) => (
                  <div key={index} className="card">
                    <div className="p-6 flex gap-4">
                      <div className="flex-shrink-0">
                        <div className="w-16 h-16 bg-safari-green rounded-full flex items-center justify-center text-white font-bold text-sm text-center leading-tight p-1">
                          {step.time}
                        </div>
                      </div>
                      <div className="flex-grow">
                        <h3 className="text-xl font-bold text-safari-green mb-2">{step.title}</h3>
                        <p className="text-gray-700">{step.desc}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </section>

            {/* What to Bring */}
            <section className="mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-safari-green mb-8 text-center">
                What to Bring
              </h2>
              <div className="grid md:grid-cols-2 gap-8">
                <div className="card p-6">
                  <h3 className="text-xl font-bold text-safari-green mb-4 flex items-center gap-2">
                    <FontAwesomeIcon icon="fa-shirt" className="w-6 h-6" />
                    Clothing Essentials
                  </h3>
                  <ul className="space-y-3 text-gray-700">
                    <li className="flex items-start gap-2">
                      <FontAwesomeIcon icon="fa-check" className="w-4 h-4 text-safari-gold mt-1" />
                      <span><strong>Quick-dry athletic clothes:</strong> You WILL get soaked! Avoid cotton—wear synthetic or wool fabrics that dry quickly.</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <FontAwesomeIcon icon="fa-check" className="w-4 h-4 text-safari-gold mt-1" />
                      <span><strong>Long pants:</strong> Essential to protect legs from rope burns and rock scrapes. Leggings or athletic pants work well.</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <FontAwesomeIcon icon="fa-check" className="w-4 h-4 text-safari-gold mt-1" />
                      <span><strong>Dry change of clothes:</strong> Pack in a waterproof bag for after the activity. You'll appreciate dry clothes for the return journey.</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <FontAwesomeIcon icon="fa-check" className="w-4 h-4 text-safari-gold mt-1" />
                      <span><strong>Light jacket:</strong> Mornings can be cool at this altitude (1,800m). Bring a windbreaker or fleece for the hike up.</span>
                    </li>
                  </ul>
                </div>

                <div className="card p-6">
                  <h3 className="text-xl font-bold text-safari-green mb-4 flex items-center gap-2">
                    <FontAwesomeIcon icon="fa-hiking" className="w-6 h-6" />
                    Footwear & Gear
                  </h3>
                  <ul className="space-y-3 text-gray-700">
                    <li className="flex items-start gap-2">
                      <FontAwesomeIcon icon="fa-check" className="w-4 h-4 text-safari-gold mt-1" />
                      <span><strong>Sturdy hiking boots or shoes:</strong> Good ankle support and grip essential for the hike and descent. Boots will get wet.</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <FontAwesomeIcon icon="fa-check" className="w-4 h-4 text-safari-gold mt-1" />
                      <span><strong>Gloves:</strong> Provided by guides, but you can bring your own if you prefer. Protects hands from rope friction.</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <FontAwesomeIcon icon="fa-check" className="w-4 h-4 text-safari-gold mt-1" />
                      <span><strong>Small daypack:</strong> For carrying water, snacks, and extra layers during the hike. Waterproof or with rain cover recommended.</span>
                    </li>
                  </ul>
                </div>

                <div className="card p-6">
                  <h3 className="text-xl font-bold text-safari-green mb-4 flex items-center gap-2">
                    <FontAwesomeIcon icon="fa-camera" className="w-6 h-6" />
                    Photography & Electronics
                  </h3>
                  <ul className="space-y-3 text-gray-700">
                    <li className="flex items-start gap-2">
                      <FontAwesomeIcon icon="fa-check" className="w-4 h-4 text-safari-gold mt-1" />
                      <span><strong>Waterproof camera or GoPro:</strong> Essential for capturing the descent! Regular cameras will get destroyed by the spray.</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <FontAwesomeIcon icon="fa-check" className="w-4 h-4 text-safari-gold mt-1" />
                      <span><strong>Waterproof phone case:</strong> If you want to bring your phone. Secure it well—dropping it means it's gone forever.</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <FontAwesomeIcon icon="fa-check" className="w-4 h-4 text-safari-gold mt-1" />
                      <span><strong>Extra batteries:</strong> Cold and wet conditions drain batteries faster. Bring fully charged spares.</span>
                    </li>
                  </ul>
                </div>

                <div className="card p-6">
                  <h3 className="text-xl font-bold text-safari-green mb-4 flex items-center gap-2">
                    <FontAwesomeIcon icon="fa-briefcase" className="w-6 h-6" />
                    Other Essentials
                  </h3>
                  <ul className="space-y-3 text-gray-700">
                    <li className="flex items-start gap-2">
                      <FontAwesomeIcon icon="fa-check" className="w-4 h-4 text-safari-gold mt-1" />
                      <span><strong>Water and snacks:</strong> 1-2 liters of water and energy bars or fruit for the hike. Stay hydrated!</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <FontAwesomeIcon icon="fa-check" className="w-4 h-4 text-safari-gold mt-1" />
                      <span><strong>Sunscreen and hat:</strong> High altitude means stronger UV exposure. Protect yourself even on cloudy days.</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <FontAwesomeIcon icon="fa-check" className="w-4 h-4 text-safari-gold mt-1" />
                      <span><strong>Sense of adventure:</strong> Most important! Come ready for an unforgettable, wet, wild experience.</span>
                    </li>
                  </ul>
                </div>
              </div>
            </section>

            {/* FAQs */}
            <section className="mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-safari-green mb-12 text-center">
                Common Questions
              </h2>
              <div className="space-y-6">
                <div className="border-l-4 border-safari-gold pl-6 py-4">
                  <h3 className="text-xl font-bold text-safari-green mb-2">
                    Is abseiling at Sipi Falls safe?
                  </h3>
                  <p className="text-gray-700">
                    Very safe when done with professional operators. All guides are certified with years of experience, and equipment meets international standards with regular inspections. Multiple safety redundancies are built into every rope system—you're attached to two independent anchor points, and guides manage your descent from both above and below. The rope systems can hold many times your body weight. That said, this is an adventure activity with inherent risks, which is why following all safety instructions is crucial. Thousands of people have safely abseiled Sipi Falls over the years.
                  </p>
                </div>

                <div className="border-l-4 border-safari-gold pl-6 py-4">
                  <h3 className="text-xl font-bold text-safari-green mb-2">
                    Do I need previous climbing or abseiling experience?
                  </h3>
                  <p className="text-gray-700">
                    No! The vast majority of participants are first-timers with zero climbing experience. Your guides provide comprehensive training on proper technique before you start, including how to control your descent, use your brake hand, position your body, and communicate with hand signals. You'll practice on a smaller section first before tackling the main waterfall. The guides control the rope system and can slow or stop your descent at any time if needed. If you can walk backward and follow instructions, you can abseil Sipi Falls.
                  </p>
                </div>

                <div className="border-l-4 border-safari-gold pl-6 py-4">
                  <h3 className="text-xl font-bold text-safari-green mb-2">
                    What if I'm afraid of heights?
                  </h3>
                  <p className="text-gray-700">
                    Some nervousness is completely normal—even experienced climbers feel butterflies before a 100-meter descent! Many people find that the controlled nature of abseiling, where you're always in contact with the rock face and can control your speed, actually helps overcome mild fear of heights. However, if you have severe acrophobia (extreme fear of heights), this activity might not be for you. The initial moment of leaning back over the edge is the hardest part, but once you're descending, most people find it exhilarating rather than terrifying. You can always stop, rest, and catch your breath during the descent.
                  </p>
                </div>

                <div className="border-l-4 border-safari-gold pl-6 py-4">
                  <h3 className="text-xl font-bold text-safari-green mb-2">
                    Will I get wet? What about the weather?
                  </h3>
                  <p className="text-gray-700">
                    Absolutely, you WILL get soaked! You're abseiling beside a waterfall—expect to be drenched by spray, mist, and sometimes direct water flow. Getting wet is part of the adventure and actually makes it more thrilling. Wear quick-dry clothes and bring a dry change for afterward. Regarding weather, abseiling operates year-round, though the dry seasons (June-September, December-February) offer the most reliable conditions. During rainy season, the falls are more powerful and spectacular but trails can be muddy. Heavy rain or lightning will cancel activities for safety, but light rain doesn't stop the fun—you're getting wet anyway!
                  </p>
                </div>

                <div className="border-l-4 border-safari-gold pl-6 py-4">
                  <h3 className="text-xl font-bold text-safari-green mb-2">
                    What are the age, weight, and fitness requirements?
                  </h3>
                  <p className="text-gray-700">
                    The minimum age is typically 10 years old, though some operators may allow younger children if they're mature and tall enough. There's a maximum weight limit of approximately 110kg (240 lbs) due to equipment specifications. Regarding fitness, you need to be able to hike 45-60 minutes uphill to reach the top of the falls, which requires moderate fitness. The abseiling itself doesn't require strength—the rope holds your weight—but you need to be able to walk backward down the rock face and control your descent. The activity is not suitable for pregnant women or people with serious heart conditions, back problems, or recent surgeries.
                  </p>
                </div>

                <div className="border-l-4 border-safari-gold pl-6 py-4">
                  <h3 className="text-xl font-bold text-safari-green mb-2">
                    Can I combine abseiling with other activities at Sipi Falls?
                  </h3>
                  <p className="text-gray-700">
                    Absolutely! Most visitors combine abseiling with other Sipi Falls experiences. The three-waterfall circuit hike is popular, taking you to all three falls (100m, 85m, and 65m) through beautiful coffee plantations. Coffee tours are highly recommended—local farmers guide you through the entire "seed to cup" process, from picking cherries to roasting and tasting the final brew. You can also add rock climbing on nearby cliffs, cultural visits to Sabiny villages, or simply relax at one of the lodges with spectacular views. A full day at Sipi Falls combining abseiling, waterfall hikes, and a coffee tour makes for an unforgettable experience.
                  </p>
                </div>
              </div>
            </section>

            {/* Related Activities */}
            <section>
              <h2 className="text-3xl md:text-4xl font-bold text-safari-green mb-8 text-center">
                More Eastern Uganda Adventures
              </h2>
              <div className="grid md:grid-cols-2 gap-8">
                <Link to="/activities/whitewater-rafting" className="card group hover:shadow-xl transition-all duration-300">
                  <div className="relative h-48 overflow-hidden">
                    <img
                      src="https://images.unsplash.com/photo-1530866495561-507c9faab2ed?w=800"
                      alt="Whitewater Rafting"
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                    />
                  </div>
                  <div className="p-6">
                    <h3 className="text-xl font-bold text-safari-green mb-2">Whitewater Rafting</h3>
                    <p className="text-gray-700 mb-4">Conquer the Nile's world-class rapids in Jinja for the ultimate water adventure.</p>
                    <p className="text-safari-gold font-bold">Activity fees apply</p>
                  </div>
                </Link>

                <Link to="/activities/mount-elgon-trekking" className="card group hover:shadow-xl transition-all duration-300">
                  <div className="relative h-48 overflow-hidden">
                    <img
                      src="https://images.unsplash.com/photo-1464822759023-fed622ff2c3b?w=800"
                      alt="Mount Elgon Trekking"
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                    />
                  </div>
                  <div className="p-6">
                    <h3 className="text-xl font-bold text-safari-green mb-2">Mount Elgon Trekking</h3>
                    <p className="text-gray-700 mb-4">Trek to Wagagai Peak (4,321m) and explore Africa's largest volcanic caldera.</p>
                    <p className="text-safari-gold font-bold">Activity fees apply</p>
                  </div>
                </Link>
              </div>
            </section>
          </main>

          {/* Sidebar */}
          <aside className="lg:col-span-1">
            <div className="sticky top-24">
              <div className="card p-6 mb-8">
                <h3 className="text-2xl font-bold text-safari-green mb-4">Activity Details</h3>
                <div className="space-y-4 text-gray-700">
                  <div className="flex items-center gap-3">
                    <FontAwesomeIcon icon="fa-clock" className="w-6 h-6 text-safari-green" />
                    <span><strong>Duration:</strong> 4-6 hours</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <FontAwesomeIcon icon="fa-mountain" className="w-6 h-6 text-safari-green" />
                    <span><strong>Descent:</strong> 100 meters (328 feet)</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <FontAwesomeIcon icon="fa-calendar" className="w-6 h-6 text-safari-green" />
                    <span><strong>Best Time:</strong> Jun-Sep, Dec-Feb</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <FontAwesomeIcon icon="fa-dollar-sign" className="w-6 h-6 text-safari-green" />
                    <span><strong>Activity Fee:</strong> Contact for current rates</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <FontAwesomeIcon icon="fa-chart-simple" className="w-6 h-6 text-safari-green" />
                    <span><strong>Difficulty:</strong> Moderate</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <FontAwesomeIcon icon="fa-users" className="w-6 h-6 text-safari-green" />
                    <span><strong>Group Size:</strong> 2-10 people</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <FontAwesomeIcon icon="fa-user" className="w-6 h-6 text-safari-green" />
                    <span><strong>Min Age:</strong> 10 years</span>
                  </div>
                </div>
                <button
                  onClick={() => setIsModalOpen(true)}
                  className="bg-safari-gold hover:bg-safari-gold/90 text-white px-8 py-4 rounded-full text-lg font-bold transition-all duration-300 shadow-lg hover:shadow-xl w-full mt-6"
                >
                  Book Your Adventure
                </button>
              </div>

              <div className="card p-6 mb-8">
                <h3 className="text-2xl font-bold text-safari-green mb-6 flex items-center gap-2">
                  <FontAwesomeIcon icon="fa-check-circle" className="w-6 h-6 text-green-600" />
                  What's Included
                </h3>
                <ul className="space-y-3">
                  {[
                    "All abseiling equipment (harness, helmet, ropes)",
                    "Certified professional guides",
                    "Comprehensive safety briefing and training",
                    "Waterfall circuit hike",
                    "Certificate of achievement",
                    "Safety gear and gloves"
                  ].map((item, index) => (
                    <li key={index} className="flex items-start gap-3">
                      <FontAwesomeIcon icon="fa-check" className="w-5 h-5 text-green-600 mt-1 flex-shrink-0" />
                      <span className="text-gray-700">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="card p-6 mb-8">
                <h3 className="text-2xl font-bold text-safari-green mb-6 flex items-center gap-2">
                  <FontAwesomeIcon icon="fa-times-circle" className="w-6 h-6 text-red-600" />
                  What's Not Included
                </h3>
                <ul className="space-y-3">
                  {[
                    "Transport to/from Sipi Falls",
                    "Meals and drinks",
                    "Personal travel insurance",
                    "Tips for guides",
                    "Coffee tours (can be added)"
                  ].map((item, index) => (
                    <li key={index} className="flex items-start gap-3">
                      <FontAwesomeIcon icon="fa-times" className="w-5 h-5 text-red-600 mt-1 flex-shrink-0" />
                      <span className="text-gray-700">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="card p-6">
                <h3 className="text-xl font-bold text-safari-green mb-4 flex items-center gap-2">
                  <FontAwesomeIcon icon="fa-heartbeat" className="w-6 h-6" />
                  Health & Safety
                </h3>
                <ul className="space-y-3 text-gray-700">
                  <li className="flex items-start gap-2">
                    <FontAwesomeIcon icon="fa-check-circle" className="w-5 h-5 text-safari-green mt-0.5" />
                    <span><strong>Fitness:</strong> Moderate. Must be able to hike 45-60 minutes uphill.</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <FontAwesomeIcon icon="fa-check-circle" className="w-5 h-5 text-safari-green mt-0.5" />
                    <span><strong>Weight Limit:</strong> Maximum 110kg (240 lbs) for equipment safety.</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <FontAwesomeIcon icon="fa-check-circle" className="w-5 h-5 text-safari-green mt-0.5" />
                    <span><strong>Health:</strong> Not suitable for pregnancy, serious heart/back conditions.</span>
                  </li>
                </ul>
              </div>
            </div>
          </aside>
        </div>
      </div>

      <BookingModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        experience="Sipi Falls Abseiling Adventure"
      />
    </div>
  )
}
